import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const iso = (d) => d.toISOString().slice(0, 10);
const inRange = (day, r) => r.start <= day && day <= r.end;

export default function AvailabilityCalendar({ booked = [], blackouts = [] }) {
  const [offset, setOffset] = useState(0);

  const { label, cells } = useMemo(() => {
    const base = new Date();
    const first = new Date(base.getFullYear(), base.getMonth() + offset, 1);
    const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const blanks = first.getDay();
    const arr = [];
    for (let i = 0; i < blanks; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(iso(new Date(first.getFullYear(), first.getMonth(), d)));
    return {
      label: first.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
      cells: arr,
    };
  }, [offset]);

  const today = iso(new Date());

  const statusOf = (day) => {
    if (day < today) return "past";
    if (blackouts.some((r) => inRange(day, r))) return "blackout";
    if (booked.some((r) => inRange(day, r))) return "booked";
    return "free";
  };

  const dayCls = {
    past: "text-muted-foreground/40",
    free: "text-foreground hover:bg-emerald-50",
    booked: "bg-[#C71E2E]/90 text-white font-semibold",
    blackout: "bg-[#1A1A1A]/70 text-white/80",
  };

  return (
    <div data-testid="availability-calendar" className="rounded-3xl border border-border bg-white p-5 sm:p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between">
        <button data-testid="cal-prev" onClick={() => setOffset((o) => Math.max(0, o - 1))} disabled={offset === 0} className="w-9 h-9 rounded-full flex items-center justify-center border border-border hover:bg-black/5 disabled:opacity-30 transition-colors duration-200" aria-label="Previous month">
          <ChevronLeft size={16} />
        </button>
        <p data-testid="cal-month-label" className="font-display text-lg tracking-tight">{label}</p>
        <button data-testid="cal-next" onClick={() => setOffset((o) => Math.min(11, o + 1))} disabled={offset === 11} className="w-9 h-9 rounded-full flex items-center justify-center border border-border hover:bg-black/5 disabled:opacity-30 transition-colors duration-200" aria-label="Next month">
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="mt-4 grid grid-cols-7 gap-1 text-center text-[10px] font-ui uppercase tracking-wider text-muted-foreground">
        {DOW.map((d) => <span key={d}>{d}</span>)}
      </div>
      <div className="mt-1.5 grid grid-cols-7 gap-1">
        {cells.map((day, i) =>
          day === null ? (
            <span key={`b${i}`} />
          ) : (
            <span
              key={day}
              data-testid={`cal-day-${day}`}
              data-status={statusOf(day)}
              className={`aspect-square rounded-full flex items-center justify-center text-xs font-ui transition-colors duration-150 ${dayCls[statusOf(day)]}`}
            >
              {Number(day.slice(8))}
            </span>
          )
        )}
      </div>
      <div className="mt-4 flex items-center gap-4 text-[11px] font-ui text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500/20 border border-emerald-500/50" /> Available</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#C71E2E]/90" /> Booked</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#1A1A1A]/70" /> Blocked</span>
      </div>
    </div>
  );
}
